import { useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router'
import { AppShell } from '../../app/AppShell'
import { NoticeList } from '../../app/NoticeList'
import { ApiError } from '../../api/problem'
import { useComicSheet, useDeleteComic } from '../../domains/comics/queries'
import { useComicEditing } from '../../domains/comics/useComicEditing'
import { ComicScenesCard } from '../../domains/comics/sections/ComicScenesCard'
import type { ComicCoreSection } from '../../domains/comics/types'
import { Banner, Button, Card, Input } from '../../design/components'
import styles from './comics.module.css'

/**
 * One comic: the case it draws, its scenes, and the pages under them.
 *
 * Members read; the Game Master writes — the server's `canManage` decides, and every
 * affordance on this page hangs off it rather than off anything guessed from the data.
 */
export function ComicDetailPage() {
  const params = useParams<{ comicId: string }>()
  const comicId = Number(params.comicId)

  const query = useComicSheet(comicId)
  const core = query.data?.sections.core
  const editing = useComicEditing(comicId, query.data?.canManage === true)

  if (Number.isNaN(comicId)) {
    return <ComicMessage message="That comic id is not a number." />
  }

  if (query.isPending) {
    return <ComicMessage message="Loading…" subtitle="FETCHING COMIC" />
  }

  if (query.isError) {
    const missing =
      query.error instanceof ApiError &&
      (query.error.kind === 'not-found' || query.error.kind === 'forbidden')

    return (
      <ComicMessage
        message={
          missing ? 'No such comic, or it is not one of yours.' : 'Could not load this comic.'
        }
        tone={missing ? 'info' : 'error'}
      />
    )
  }

  const sheet = query.data

  return (
    <AppShell title={core?.name ?? 'Comic'} subtitle={subtitleFor(core)}>
      <NoticeList notices={editing.notices} failure={editing.failure} onDismiss={editing.dismiss} />

      {core !== undefined && (
        <Card variant="aspects" label="Drawn from">
          <p className={styles.note}>
            {core.caseName === null || core.caseName === ''
              ? 'A case in this campaign.'
              : `“${core.caseName}” — the case this comic retells.`}
          </p>
          <Link to={`/cases/${core.caseId}`} className={styles.backLink}>
            <Button variant="secondary" fullWidth>
              Open the case
            </Button>
          </Link>
        </Card>
      )}

      <ComicScenesCard core={core} scenes={sheet.sections.scenes} editing={editing} />

      {sheet.canManage && core !== undefined && (
        <GameMasterCard comicId={comicId} core={core} editing={editing} />
      )}

      <Link to={`/campaigns/${sheet.campaignId}/comics`} className={styles.backLink}>
        <Button variant="secondary" fullWidth>
          All comics
        </Button>
      </Link>
    </AppShell>
  )
}

function GameMasterCard({
  comicId,
  core,
  editing,
}: {
  comicId: number
  core: ComicCoreSection
  editing: ReturnType<typeof useComicEditing>
}) {
  const navigate = useNavigate()
  const remove = useDeleteComic()

  const [renaming, setRenaming] = useState(false)
  const [name, setName] = useState(core.name)
  const [confirming, setConfirming] = useState(false)

  return (
    <Card variant="stunts" label="Game Master">
      {renaming ? (
        <form
          className={styles.form}
          onSubmit={(event) => {
            event.preventDefault()
            // `caseId` is required by the contract and repointing a comic at another case
            // is a different intention entirely, so the rename sends the case unchanged.
            editing.update.mutate(
              { name, caseId: core.caseId },
              { onSuccess: () => setRenaming(false) },
            )
          }}
        >
          <Input label="Name" value={name} autoFocus onChange={(e) => setName(e.target.value)} />
          <div className={styles.formActions}>
            <Button
              variant="secondary"
              onClick={() => {
                setName(core.name)
                setRenaming(false)
              }}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={editing.update.isPending || name.trim() === ''}>
              {editing.update.isPending ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </form>
      ) : (
        <div className={styles.formActions}>
          <Button variant="secondary" onClick={() => setRenaming(true)}>
            Rename the comic
          </Button>
        </div>
      )}

      <div className={styles.danger}>
        {confirming ? (
          <>
            <p className={styles.note}>
              Deleting the comic removes its scenes, their pages and every page image. The case it
              draws and the scenes it references are untouched.
            </p>
            <div className={styles.formActions}>
              <Button variant="secondary" onClick={() => setConfirming(false)}>
                Keep it
              </Button>
              <Button
                variant="danger"
                disabled={remove.isPending}
                onClick={() =>
                  remove.mutate(comicId, {
                    onSuccess: () => void navigate(`/campaigns/${core.campaignId}/comics`),
                  })
                }
              >
                {remove.isPending ? 'Deleting…' : 'Delete comic'}
              </Button>
            </div>
          </>
        ) : (
          <Button variant="danger" fullWidth onClick={() => setConfirming(true)}>
            Delete comic
          </Button>
        )}
      </div>
    </Card>
  )
}

function ComicMessage({
  message,
  subtitle,
  tone = 'info',
}: {
  message: string
  subtitle?: string
  tone?: 'info' | 'error'
}) {
  return (
    <AppShell title="Comic" {...(subtitle !== undefined && { subtitle })}>
      <Card>
        <Banner tone={tone}>{message}</Banner>
      </Card>
      <Link to="/campaigns" className={styles.backLink}>
        <Button variant="secondary" fullWidth>
          All campaigns
        </Button>
      </Link>
    </AppShell>
  )
}

/** e.g. "3 SCENES · 12 PAGES · FRONT COVER". */
function subtitleFor(core: ComicCoreSection | undefined): string | undefined {
  if (core === undefined) return undefined

  const parts = [
    core.sceneCount === 1 ? '1 scene' : `${core.sceneCount} scenes`,
    core.pageCount === 1 ? '1 page' : `${core.pageCount} pages`,
  ]

  // The cover flags are the comic's own rule made visible: at most one of each, and
  // whether it has them is the first thing a Game Master looks for.
  if (core.hasFrontCover) parts.push('front cover')
  if (core.hasBackCover) parts.push('back cover')

  return parts.join(' · ')
}
